import {useEffect, useRef, useState} from 'react';
import {Alert} from 'react-native';

import HomeSectionsService from '../../../services/HomeSectionsService';
import SuggestedFillService, {
  SuggestedFillItem,
} from '../../../services/SuggestedFillService';
import {useBagStore} from '../../../store/BagStore';

/**
 * Quantos SKUs pedir de uma vez. O pedido sugerido de uma loja é pequeno
 * (dezenas de itens), então uma página só resolve — mas o endpoint é paginado
 * e sem um per-page alto ele corta em 30.
 */
const PAGE_SIZE = 200;

/**
 * Marca a sacola como montada pelo app, e não pelo cliente. O job de carrinho
 * abandonado ignora carrinhos com esta origem: o cliente já recebeu o push do
 * pedido sugerido: mandar "você deixou itens no carrinho" no dia seguinte
 * seria dizer que ele fez algo que quem fez foi o app.
 */
const CART_ORIGIN_SUGGESTED_FILL = 'suggested_fill';

interface Options {
  /** `route.params.suggestedOrder` da tela da sacola. */
  enabled?: boolean;
  clientCode?: string;
  /** Espelha o carrinho no servidor depois de montar. */
  sendCart: (products: any[], origin?: string) => Promise<void>;
  /** Limpa o param para o efeito não repetir a cada foco da tela. */
  onFinish: () => void;
  /** Chamado quando não há nenhum produto sugerido disponível hoje. */
  onEmpty?: () => void;
}

/**
 * Monta a sacola com o pedido sugerido do dia (CA-03).
 *
 * A fonte é `/catalog/home-sections/suggested/all-products`, que já resolve a
 * tabela de preço padrão do cliente e descarta item sem estoque ou sem preço
 * (CA-04). Estoque, preço e pedido mínimo continuam sendo validados no
 * "Continuar" da sacola (CA-06) — aqui só populamos o BagStore.
 *
 * Merge sem baixar quantidade: item novo entra com a quantidade sugerida; item
 * que já está na sacola em quantidade menor sobe para a sugerida; item que o
 * cliente já colocou em quantidade maior fica como está.
 */
export function useSuggestedOrderFill({
  enabled,
  clientCode,
  sendCart,
  onFinish,
  onEmpty,
}: Options) {
  const [loading, setLoading] = useState(false);
  const hasRunRef = useRef(false);

  useEffect(() => {
    // A tela da sacola é uma aba e fica montada. Rearmar quando o param sai
    // permite que um segundo toque na notificação monte de novo.
    if (!enabled) {
      hasRunRef.current = false;
      return;
    }

    if (hasRunRef.current || !clientCode) {
      return;
    }

    hasRunRef.current = true;
    setLoading(true);

    const fill = async () => {
      try {
        const response = await HomeSectionsService.getSuggestedAllProducts(
          clientCode,
          PAGE_SIZE,
          1,
        );
        const suggested = response?.products ?? [];

        if (suggested.length === 0) {
          onEmpty?.();
          return;
        }

        const {addProduct, updateProductQuantity} = useBagStore.getState();
        let added = 0;
        const snapshot: SuggestedFillItem[] = [];

        suggested.forEach((product: any) => {
          const quantity = Number(product?.suggestion_qtd ?? 0);
          if (!product?.sku || quantity <= 0) {
            return;
          }

          snapshot.push({sku: product.sku, quantity});

          // Mesma chave usada em Home.handleAddBag: item promocional ocupa uma
          // linha própria na sacola.
          const cartKey = product.is_promotion
            ? `${product.sku}_promo`
            : product.sku;

          // Lê o estado a cada item: addProduct/updateProductQuantity mudam a
          // lista, então um snapshot de fora do loop ficaria obsoleto.
          const existing = useBagStore
            .getState()
            .products.find((p: any) => (p.cartKey ?? p.sku) === cartKey);

          if (!existing) {
            addProduct({...product, quantity, cartKey});
            added += 1;
          } else if (existing.quantity < quantity) {
            updateProductQuantity(cartKey, quantity);
            added += 1;
          }
        });

        if (added === 0) {
          return;
        }

        // Marca a sacola: o pedido que sair dela vai como pedido sugerido.
        SuggestedFillService.registerSuggestedFill(
          clientCode,
          'bag_auto',
          snapshot,
        );

        await sendCart(
          useBagStore.getState().products,
          CART_ORIGIN_SUGGESTED_FILL,
        ).catch(error =>
          console.error('Erro ao enviar o carrinho sugerido:', error),
        );
      } catch (error) {
        console.error('Erro ao montar o pedido sugerido:', error);
        Alert.alert(
          'Não conseguimos montar seu pedido sugerido',
          'Tente novamente em alguns instantes.',
        );
      } finally {
        setLoading(false);
        onFinish();
      }
    };

    fill();
  }, [enabled, clientCode, sendCart, onFinish, onEmpty]);

  return {loadingSuggestedOrder: loading};
}
